'use client';

import { useEffect, useState } from 'react';
import useOneOnOneStore from '@/lib/zustand/store/oneOnOneStore';
import NumberFlow from '@number-flow/react';
import format from 'date-fns-tz/format';
import { AnimatePresence, motion } from 'framer-motion';
import { DebugMediaItem } from '.';
import BookingForm from './components/BookingForm';
import Calendar from './components/Calendar';
import TimeList from './components/TimeList';
import TimezonePicker from './components/TimeZonePicker';
import { OWNER_AVAILABILITY } from './config';
import { DateTimeInterval } from './types/types';
import Day from './utils/day';
import getAvailability from './utils/getAvailablity';
import getBusyTimes from './utils/getBusyTimes';
import getPotentialTimes from './utils/getPottentialTimes';
import { getDateRangeInterval, mapDatesToStrings, mapStringsToDates } from './utils/helpers';
import localeDayString from './utils/locale';

function OneOnOne({ title, price, icon, description }: DebugMediaItem) {
  const { modal, duration, selectedDate, setSelectedDate, timeZone } = useOneOnOneStore();
  const start = Day.todayWithOffset(2);
  const end = Day.todayWithOffset(25);
  const startDay = Day.dayFromString(start.toString());
  const endDay = Day.dayFromString(end.toString());

  const potential = getPotentialTimes({
    start: startDay,
    end: endDay,
    duration,
    availabilitySlots: OWNER_AVAILABILITY,
  });

  const [busy, setBusy] = useState<any>(null);
  const [renderKey, setRenderKey] = useState<number>(100);

  useEffect(() => {
    const interval = setTimeout(() => {
      setRenderKey(price);
    }, 300);
    return () => clearTimeout(interval);
  }, [price]);

  useEffect(() => {
    const fetchBusyTimes = async () => {
      const busyTimes = await getBusyTimes(
        getDateRangeInterval({
          start,
          end,
          timeZone,
        })
      );
      setBusy(mapDatesToStrings(busyTimes));
    };

    fetchBusyTimes();
  }, []);

  const offers = getAvailability({
    busy: mapStringsToDates(busy),
    potential,
  });

  const slots = offers.filter(slot => {
    return (
      slot.start >= startDay.toInterval('Etc/GMT').start &&
      slot.end <= endDay.toInterval('Etc/GMT').end
    );
  });

  // If we got this far and there's no selectedDate, set it to the first date
  // with some availability.
  useEffect(() => {
    if (!selectedDate && slots.length > 0) {
      const date: Date = slots[0].start;
      const dateString: string = localeDayString(date);
      setSelectedDate(Day.dayFromString(dateString));
    }
    // Run once, on initial render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let maximumAvailability = 0;
  const availabilityByDate = slots.reduce<Record<string, DateTimeInterval[]>>((acc, slot) => {
    // Gives us the same YYYY-MM-DD format as Day.toString()
    const date = format(slot.start, 'yyyy-MM-dd', { timeZone });

    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(slot);

    if (acc[date].length > maximumAvailability) {
      maximumAvailability = acc[date].length;
    }
    return acc;
  }, {});

  const availability = selectedDate ? availabilityByDate[selectedDate.toString()] : [];

  return (
    <div className='relative flex flex-col lg:flex-row justify-center gap-10 h-full overflow-y-auto'>
      <div className='w-full lg:w-1/3 space-y-2 h-full max-sm:text-left'>
        <div className='flex items-center gap-2'>
          <p>Topic:</p>
          <p className='text-lg font-semibold'>{title}</p>
        </div>
        <p className='text-sm'>{description}</p>
        {/* <h2 className='text-semibold font-medium text-slate-300'>₹{price}</h2> */}
        <NumberFlow
          className='text-semibold font-medium text-slate-300 text-4xl'
          value={renderKey}
          format={{ style: 'currency', currency: 'INR', trailingZeroDisplay: 'stripIfInteger' }}
        />
      </div>

      <AnimatePresence mode='wait'>
        {modal === 'closed' && (
          <motion.div
            key='calendar-content'
            className='flex flex-col gap-2 flex-1 w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -500 }}
            transition={{ duration: 0.5 }}
          >
            <div className='flex gap-2'>
              {/* <DurationPicker /> */}
              <TimezonePicker />
            </div>
            <Calendar offers={availabilityByDate} maximumAvailability={maximumAvailability} />
            {selectedDate && <TimeList availability={availability} />}
          </motion.div>
        )}

        {/* {modal !== 'closed' && ( */}
        <motion.div
          key='booking-form'
          className={`flex flex-col gap-2 flex-1 overflow-clip ${modal == 'closed' ? 'hidden' : ''}`}
          initial={{ opacity: 0, y: 500 }} // Slide in from the bottom
          animate={{ opacity: 1, y: 0 }} // Animate to visible
          exit={{ opacity: 0, y: 500 }} // Slide out to the bottom
          transition={{ duration: 0.5 }}
        >
          <BookingForm title={title} price={price} description={description} />
        </motion.div>
        {/* // )} */}
      </AnimatePresence>
    </div>
  );
}

export default OneOnOne;
